import type { RouteLocationNamedRaw } from 'vue-router'
import type { CmsSsbtBootstrapResponse } from '@nsftx/seven-client-retail-vue-sgp'
import { logService } from '@nsftx/seven-client-retail-utils'

type LandingRouteQuery = Record<string, string | undefined>

const homeRoute = (query?: LandingRouteQuery): RouteLocationNamedRaw => ({
  name: 'home',
  query
})

const LOG_PREFIX = '[resolveLandingRoute]'

/**
 * Resolves the SSBT start / return-home route from CMS `deviceSettings.landingPage`.
 * Falls back to the main menu (`home`) when unset, unknown, or the target is missing.
 */
export const resolveLandingRoute = (
  cmsConfig: CmsSsbtBootstrapResponse | null | undefined,
  query?: LandingRouteQuery
): RouteLocationNamedRaw => {
  const landingPage = cmsConfig?.deviceSettings?.landingPage

  if (!landingPage?.value) {
    logService.debug(`${LOG_PREFIX} landing page is missing or not set`, {
      code: 'ST_LANDING_PAGE_NOT_SET',
      landingPage
    })
    return homeRoute(query)
  }

  if (landingPage.relationTo === 'ssbt-collections') {
    const collectionExists = cmsConfig?.collections?.docs.some(
      (collection) => collection.id === landingPage.value
    )

    if (collectionExists) {
      return {
        name: 'collection',
        params: { id: landingPage.value },
        query
      }
    }

    logService.warn(`${LOG_PREFIX} collection not found`, {
      code: 'ST_LANDING_PAGE_COLLECTION_NOT_FOUND',
      landingPage
    })
  }

  if (landingPage.relationTo === 'gm-games') {
    const gameLink = cmsConfig?.games?.docs.find(
      (link) => String(link.installation.game.id) === landingPage.value
    )

    if (gameLink) {
      return {
        name: 'game',
        params: { linkCode: gameLink.code },
        query
      }
    }

    logService.warn(`${LOG_PREFIX} game not found`, {
      code: 'ST_LANDING_PAGE_GAME_NOT_FOUND',
      landingPage
    })
  }

  return homeRoute(query)
}
